import { InvitationStatus } from '../../generated/api/invitationStatus.js';
import { ContactRole, ContactType, DuplicateContactResult, UserProfile } from '../../generated/api/models.js';
import { openDialog } from '../../public/site/dialogStore';
import { ContactListFields, DialogType } from '../enums.js';
import { createNewMsg, notAbleToProcessContactRequestMsg, pleaseContactCustomerServiceMsg, t } from '../i18n/index.js';
import type { UpsertContactResult } from '../../components/ContactsEditContext/ContactsEditContext.js';
import type { ContactInvitation } from '../../generated/api/contactInvitation.js';
import type {
  Contact,
  ContactPerson,
  DuplicateContact,
  GenericErrorDuplicateContact,
  PutContactResponse,
} from '../../generated/api/models.js';
import type { FormattedContactOption } from '../types/checkout.js';
import type { DialogParams } from '../types/dialog.js';
import type { Dispatch } from 'redux';

const toDisplayValue = (firstName: string, lastName: string | undefined) => {
  if (firstName && lastName) {
    return firstName + ' ' + lastName;
  }
  return firstName;
};

export const sortContactsByFirstName = (contacts: Contact[]): Contact[] => {
  const sortValue = (contact: Contact): string => {
    if (contact.person) {
      return contact.person.firstName;
    }
    return contact.commonFunction ? contact.commonFunction.name : '';
  };
  return [...contacts].sort((a, b) => (sortValue(a) >= sortValue(b) ? 1 : -1));
};

export const getDuplicateContactInformationDialogParams = (
  duplicateContact: GenericErrorDuplicateContact
): DialogParams => ({
  type: DialogType.DUPLICATE_CONTACT_INFORMATION,
  duplicateContact,
});

export const getPossibleDuplicateContactInformationDialogParams = (
  duplicateContact: GenericErrorDuplicateContact
): DialogParams => ({
  type: DialogType.POSSIBLE_DUPLICATE_CONTACT_INFORMATION,
  duplicateContact,
});

export const getDuplicateContactDialogParams = (): DialogParams => ({
  type: DialogType.DUPLICATE_CONTACT,
  header: t.AYKR(pleaseContactCustomerServiceMsg),
  body: t.EGFA(notAbleToProcessContactRequestMsg),
});

export interface UpsertContactResultHandlers {
  onSuccess: (result: Extract<UpsertContactResult, { kind: 'success' }>) => void;
  onFailure: (result: Extract<UpsertContactResult, { kind: 'validationError' | 'error' }>) => void;
}

export const handleUpsertContactResult = (
  result: UpsertContactResult,
  dispatch: Dispatch,
  handlers: UpsertContactResultHandlers
): void => {
  switch (result.kind) {
    case 'validationError':
    case 'error':
      handlers.onFailure(result);
      return;
    case 'possibleDuplicate':
      openDialog(getPossibleDuplicateContactInformationDialogParams(result.duplicateContact));
      return;
    case 'duplicate':
      openDialog(getDuplicateContactInformationDialogParams(result.duplicateContact));
      return;
    case 'conflict':
      openDialog(getDuplicateContactDialogParams());
      return;
    case 'success':
      handlers.onSuccess(result);
      return;
    default: {
      const exhaustiveCheck: never = result;
      throw new Error(`Unhandled UpsertContactResult kind: ${JSON.stringify(exhaustiveCheck)}`);
    }
  }
};

export const CREATE_NEW_CONTACT = (): FormattedContactOption => ({
  displayValue: t.GFN5(createNewMsg),
  value: 'CREATE_NEW_CONTACT',
});

export const formatContactsToFieldValueOptions = (
  contacts: Contact[] | undefined,
  createNewContact: boolean,
  showEmail?: boolean
): {
  displayOptions: string[];
  options: FormattedContactOption[];
} => {
  const displayOptions: string[] = [];
  const options: FormattedContactOption[] = [];
  if (createNewContact) {
    displayOptions.push(CREATE_NEW_CONTACT().displayValue!);
    options.push(CREATE_NEW_CONTACT());
  }
  if (!contacts) {
    return { displayOptions, options };
  }
  contacts.forEach(contact => {
    if (contact?.person) {
      const displayValue: string = toDisplayValue(contact.person.firstName, contact.person.lastName);
      const value: string = contact.contactId!;
      if (displayValue && value) {
        displayOptions.push(displayValue);
        options.push({ displayValue, value, displaySubValue: showEmail ? contact.person.email : undefined });
      }
    } else if (contact?.userContact) {
      const displayValue: string = toDisplayValue(contact.userContact.firstName, contact.userContact.lastName);
      const value: string = contact.contactId!;
      if (displayValue && value) {
        displayOptions.push(displayValue);
        options.push({ displayValue, value, displaySubValue: showEmail ? contact.userContact.email : undefined });
      }
    }
  });
  return { displayOptions, options };
};

export const getFullNameFromContactPerson = (contactPerson: ContactPerson | undefined) =>
  contactPerson ? `${contactPerson.firstName} ${contactPerson.lastName}` : '';

export const getContactPersonByContactId = (
  contacts: Contact[] | undefined,
  contactId?: string
): ContactPerson | undefined =>
  contactId ? contacts?.find(contact => contact.contactId === contactId)?.person : undefined;

export const getContactFromContactPerson = (contactPerson: ContactPerson): Contact => {
  if (contactPerson.costCenter || contactPerson.employeeNumber) {
    return {
      contactType: ContactType.PERSON,
      person: contactPerson,
    };
  }
  return {
    contactType: ContactType.LIMITED_INFORMATION_PERSON,
    userContact: contactPerson,
  };
};

export const getContactSort = (sort?: string) => {
  const fullNameSort = `${ContactListFields.CONTACT_FIRST_NAME},${ContactListFields.CONTACT_LAST_NAME}`;
  return sort && sort !== ContactListFields.CONTACT_NAME ? sort : fullNameSort;
};

export const getUserInfo = (contact?: Contact) => {
  if (contact?.person) {
    return {
      firstName: contact.person.firstName,
      lastName: contact.person.lastName,
      email: contact.person.email,
      phoneNumber: contact.person.phoneNumber,
    };
  }
  if (contact?.userContact) {
    return {
      firstName: contact.userContact.firstName,
      lastName: contact.userContact.lastName,
      email: contact.userContact.email,
      phoneNumber: contact.userContact.phoneNumber,
    };
  }
  return undefined;
};

export interface DuplicateContactResponse {
  type: DuplicateContact.DuplicateTypeEnum;
  duplicateContact?: GenericErrorDuplicateContact;
}

export enum CreateContactType {
  SUBSCRIPTION = 'subscription',
  BILLING = 'billing',
  BILL_RECEIVER = 'billReceiver',
}

export const isDuplicateContactError = (response: PutContactResponse | DuplicateContactResponse) =>
  'type' in response &&
  (response.type === DuplicateContactResult.TypeEnum.DUPLICATE_CONTACT ||
    response.type === DuplicateContactResult.TypeEnum.POSSIBLE_CONTACT_DUPLICATE);

export const isConflictingContactError = (response: PutContactResponse | DuplicateContactResponse) =>
  'type' in response && response.type === DuplicateContactResult.TypeEnum.CONFLICTING_CONTACT;

export const isAdmin = (contact?: Contact) => contact?.person?.userProfile === UserProfile.KEY_USER;

export const isEmployee = (contact?: Contact) => contact?.person?.userProfile === UserProfile.EMPLOYEE;

export const isEoeInUse = (contact?: Contact) => contact?.person?.eoeInUse;

export const isCommunityUser = (contact?: Contact) => isAdmin(contact) || isEmployee(contact);

export const isOrderApprover = (contact?: Contact) => contact?.person?.roles?.includes(ContactRole.APPROVER) ?? false;

export const isBeingInvited = (invitation?: ContactInvitation) =>
  !!invitation && invitation.status !== InvitationStatus.Used && invitation.status !== InvitationStatus.Cancelled;

export const canApproveDeviceOrder = (contact?: Contact) =>
  isAdmin(contact) || isOrderApprover(contact) || isEoeInUse(contact);
